import { RouteLocationNormalized } from 'vue-router';

import { IActions } from '../interfaces/store.interfaces';
import store from '.';
import getters from './getters';
import mutations from './mutations';
import { ApplicationStatusOptions } from '../models/application-status/ApplicationStatusOptions';
import { ParameterType, SortingType } from '@/study-search/enums';
import { UrlPathConstructor } from '../models/url';
import { Indexability } from '../models/indexability/Indexability';
import { ParameterValue } from '@/study-search/domain';
import { SearchIndexabilityManager, ISeoFilterState } from '@studyportals/fawkes/programmes-search-seo';
import { SeoInfoBase } from '../models/indexability/SeoInfoBase';
import { SeoApplicationState } from '../models/indexability/SeoApplicationState';
import { EventsSender } from '../infrastructure/events';
import SearchResultsPresenter from '../presenters/SearchResultsPresenter';
import { PaginationDefinition } from '../models/definition';
import { IAutocomplete, IUrlContent } from '../interfaces';
import { router, RoutingDefinitions, RoutingHelper } from '../router';
import { UrlParser } from '../models/url';
import Tracking from '../infrastructure/tracking/Tracking';
import { LocalizedContentPlugin } from './plugins/LocalizedContentPlugin';
import { ParameterService } from '../infrastructure/ParameterService';
import { DataStorage } from '@studyportals/data-storage';
import {  FilterValuesChangedEvent } from '@studyportals/search-filters';
import {FilterKey, IFilterPayload, ISuggestionPayload} from '@studyportals/search-filters/server-side';
import { IAppState, SearchPageFilterStateChangedEvent, SearchPageClearFiltersEvent } from '@studyportals/search-page-interface';
import { EventAggregationService } from '@/search-page/platform/EventAggregationService';
import { ParsedFilters } from '../models/generalizations/ParsedFilters';
import { DisciplineFaqHandler } from '../models/faq';
import CompatibilityLayer from '../infrastructure/CompatibilityLayer';
import FilterProvider from '../presentation/components/filters/FilterProvider';
import FaqStaticContentPresenter from '../presenters/FaqStaticContentPresenter';
import { Filter } from '../models/filter';

const urlPathConstructor = new UrlPathConstructor();

const checkAndSetCanonicalUrl = async (): Promise<string | null> => {
	const indexability = Indexability.getInstance();
	const isIndexable = await indexability.shouldIndex();

	if (!isIndexable) {
		// Find the best indexable canonical URL
		return await findBestIndexableCanonicalUrl();
	}

	// Current page is indexable, use self-canonical
	return null;
};

/**
 * Creates a custom filter state for testing indexability
 */
class TestFilterState implements ISeoFilterState {
	constructor(private filters: Filter[]) {}

	public getSelectedValuesFor(key: FilterKey): readonly string[] {
		const filter = this.filters.find(f => f.key === key);
		return filter ? filter.values : [];
	}

	public getSelectedFilters(): readonly FilterKey[] {
		return this.filters.filter(f => f.values.length > 0).map(f => f.key);
	}
}

/**
 * Finds the best indexable canonical URL by trying different filter combinations
 * Uses a dynamic algorithm to find the maximum indexable subset of filters
 */
const findBestIndexableCanonicalUrl = async (): Promise<string | null> => {
	const allFilters = store.getters.getFilters().filter(f => f.values.length > 0);

	if (allFilters.length === 0) {
		const indexablePortalPath = store.getters.getPortalPath();
		return `${location.origin}/search/${indexablePortalPath}`;
	}

	// Try the full combination first
	if (await testIndexabilityForFilters(allFilters)) {
		const canonicalUrl = await generateCanonicalUrlForFilters(allFilters);
		if (canonicalUrl) {
			return canonicalUrl;
		}
	}

	// Find the best indexable combination using a more systematic approach
	const bestCombination = await findBestIndexableCombination(allFilters);

	if (bestCombination && bestCombination.length > 0) {
		const canonicalUrl = await generateCanonicalUrlForFilters(bestCombination);
		if (canonicalUrl) {
			return canonicalUrl;
		}
	}

	// Fallback to portal path
	const portalPath = store.getters.getPortalPath();
	return `${location.origin}/search/${portalPath}`;
};

/**
 * Finds the best indexable combination of filters
 * Uses a greedy approach to find the largest indexable subset
 */
const findBestIndexableCombination = async (filters: Filter[]): Promise<Filter[] | null> => {
	// Cache for tested combinations to avoid redundant checks
	const testedCombinations = new Map<string, boolean>();

	// Helper to create a unique key for a combination
	const getCombinationKey = (combination: Filter[]): string => {
		return combination
			.map(f => f.type)
			.sort()
			.join('|');
	};

	// Test and cache combination
	const testCombination = async (combination: Filter[]): Promise<boolean> => {
		const key = getCombinationKey(combination);
		if (testedCombinations.has(key)) {
			return testedCombinations.get(key)!;
		}
		const result = await testIndexabilityForFilters(combination);
		testedCombinations.set(key, result);
		return result;
	};

	// Priority order (descending)
	const getFilterWeight = (filter: Filter): number => {
		const weights: Record<string, number> = {
			[ParameterType.DEGREE_TYPE]: 16,
			[ParameterType.SPECIAL_PROGRAMMES]: 15,
			[ParameterType.TUITION_FEE]: 14,
			[ParameterType.ATTENDANCE]: 11,
			[ParameterType.DELIVERY_METHOD]: 7,
			[ParameterType.DURATION]: 4,
			[ParameterType.DISCIPLINES]: 3,
			[ParameterType.CONTINENT]: 2,
			[ParameterType.COUNTRY]: 1,
			[ParameterType.AREA]: 0,
			[ParameterType.EDUCATIONAL_FORM]: 0,
		};
		return weights[filter.type] || 0;
	};

	const sortedFilters = [...filters].sort((a, b) => getFilterWeight(b) - getFilterWeight(a));

	// Try removing one filter at a time (n-1 combinations)
	if (filters.length > 1) {
		for (let i = 0; i < sortedFilters.length; i++) {
			const combination = sortedFilters.filter((_, index) => index !== i);
			if (await testCombination(combination)) {
				return combination;
			}
		}
	}

	// Build up from most important filters
	let bestCombination: Filter[] = [];
	let currentCombination: Filter[] = [];

	for (const filter of sortedFilters) {
		const testCombo = [...currentCombination, filter];
		if (await testCombination(testCombo)) {
			currentCombination = testCombo;
			if (currentCombination.length > bestCombination.length) {
				bestCombination = [...currentCombination];
			}
		}
	}

	// Try pairs of high-priority filters
	if (bestCombination.length < 2 && sortedFilters.length >= 2) {
		for (let i = 0; i < Math.min(3, sortedFilters.length); i++) {
			for (let j = i + 1; j < sortedFilters.length; j++) {
				const combination = [sortedFilters[i], sortedFilters[j]];
				if (await testCombination(combination)) {
					if (combination.length > bestCombination.length) {
						bestCombination = combination;
					}
				}
			}
		}
	}

	// Try individual filters
	if (bestCombination.length === 0) {
		for (const filter of sortedFilters) {
			if (await testCombination([filter])) {
				bestCombination = [filter];
				break;
			}
		}
	}

	return bestCombination.length > 0 ? bestCombination : null;
};

/**
 * Tests if a filter combination would be indexable by fawkes
 * Creates a new indexability manager instance with the test filters
 */
const testIndexabilityForFilters = async (filters: Filter[]): Promise<boolean> => {
	// Create a custom filter state for testing
	const testFilterState = new TestFilterState(filters);

	// Create a new indexability manager with the test filter state
	const testIndexabilityManager = new SearchIndexabilityManager(
		window.PortalType,
		new SeoInfoBase(),
		testFilterState,
		SeoApplicationState.getInstance()
	);

	// Test indexability with the custom filter combination
	return await testIndexabilityManager.shouldIndex();
};


/**
 * Generates a canonical URL for the given filter combination
 */
const generateCanonicalUrlForFilters = async (filters: Filter[]): Promise<string | null> => {
	const routingHelper = (await import('@/study-search/router/RoutingHelper')).default;

	try {
		const url = await routingHelper.resolveToUrl(filters, {}, 1);

		if (url) {
			if (url.startsWith('/')) {
				return `${location.origin}${url}`;
			}
			return url;
		}

		return buildFallbackCanonicalUrl(filters);
	} catch {
		return buildFallbackCanonicalUrl(filters);
	}
};

/**
 * Builds a fallback canonical URL when routing fails
 */
const buildFallbackCanonicalUrl = (filters: Filter[]): string => {
	const portalPath = store.getters.getPortalPath();
	let path = `/search/${portalPath}`;

	const countryFilter = filters.find(f => f.type === ParameterType.COUNTRY);
	const disciplineFilter = filters.find(f => f.type === ParameterType.DISCIPLINES);
	const degreeTypeFilter = filters.find(f => f.type === ParameterType.DEGREE_TYPE);

	if (degreeTypeFilter && degreeTypeFilter.values.length > 0) {
		const degreeTypePath = urlPathConstructor.extractUrlPathPart(degreeTypeFilter);
		path += `/${degreeTypePath}`;
	}

	if (disciplineFilter && disciplineFilter.values.length > 0) {
		const disciplinePath = urlPathConstructor.extractUrlPathPart(disciplineFilter);
		path += `/${disciplinePath}`;
	}

	if (countryFilter && countryFilter.values.length > 0) {
		const countryPath = urlPathConstructor.extractUrlPathPart(countryFilter);
		path += `/${countryPath}`;
	}

	return `${location.origin}${path}`;
};

const getParameters = async (): Promise<ParameterValue[]> => {
	const parameterService = new ParameterService();
	return parameterService.getParameters();
};

const pageTracking = (): void => {
	const resultsFound = getters.getTotalSearchResults();

	const keywordWhatFilter = store.state.filters.filter((_) => _.type === ParameterType.KEYWORD_WHAT)[0];
	const keywordWhereFilter = store.state.filters.filter((_) => _.type === ParameterType.KEYWORD_WHERE)[0];

	const searchTerms: string[] = [];
	if (keywordWhatFilter && keywordWhatFilter.values.length) {
		searchTerms.push(keywordWhatFilter.values[0]);
	}

	if (keywordWhereFilter && keywordWhereFilter.values.length) {
		searchTerms.push(keywordWhereFilter.values[0]);
	}

	if (searchTerms.length === 0) {
		searchTerms.push('');
	}

	const filtersWithValues = store.state.filters.filter((filter) => filter.values.length > 0);

	const snowPlowData = {};
	filtersWithValues.forEach((_) => (snowPlowData[_.type] = _.values.toString()));

	Tracking.snowPlowSiteSearch(searchTerms, snowPlowData, resultsFound);
};

const navigateFromState = async (): Promise<void> => {
	await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), getters.getPageNumber());
};

const onFilterChange = async (): Promise<void> => {
	mutations.storePageNumber(PaginationDefinition.DEFAULT_PAGE_NUMBER);
	mutations.untrackStudyCardIds();
	await navigateFromState();
	DisciplineFaqHandler.Invalidated = true;
};

const publishFilterStateChanged = (parameters: ParameterValue[]): void => {
	EventAggregationService.instance.publishTo(SearchPageFilterStateChangedEvent.EventType, new SearchPageFilterStateChangedEvent());
	void publishValuesFiltersChanged(parameters)
};

const publishValuesFiltersChanged = (parameters: ParameterValue[]): void => {
	const searchInput = CompatibilityLayer.mapToProgrammeSearchInput(parameters, store.getters.getSorting(), store.getters.getPortalType());
	EventAggregationService.instance.publishTo(FilterValuesChangedEvent.EventType, new FilterValuesChangedEvent(searchInput, new FilterProvider()));
}

const actions: IActions = {
	setAreDependenciesLoaded: async (areDependenciesLoaded: boolean): Promise<void> => {
		store.mutations.setAreDependenciesLoaded(areDependenciesLoaded);

		if (areDependenciesLoaded) {
			const localizedContentPlugin = new LocalizedContentPlugin();
			localizedContentPlugin.initiate();
		}
	},

	callServices: async (): Promise<void> => {
		if (store.state.applicationStatus !== ApplicationStatusOptions.ERROR) {
			const parameters: ParameterValue[] = await getParameters();

			publishValuesFiltersChanged(parameters);

			await store.actions.fetchFaqStaticContent();

			await store.actions.updateSearchResults(parameters);

			const canonicalUrl = await checkAndSetCanonicalUrl();

			const metaRefreshInfo = getters.getMetaRefreshInfo();
			let canonicalPath = '';
			if (canonicalUrl) {
				canonicalPath = canonicalUrl;
			} else if (metaRefreshInfo) {
				canonicalPath = `${location.origin}${metaRefreshInfo}`;
			} else {
				canonicalPath = `${location.origin}${location.pathname}`;
			}
			mutations.storeCanonical(canonicalPath);
			mutations.storeApplicationStatus(ApplicationStatusOptions.READY);

			EventsSender.sendInitialEvents(store.getters.getPortalType());
			publishFilterStateChanged(parameters);
		}
	},

	fetchFaqStaticContent: async (): Promise<void> => {
		const filters = store.getters.getFilters();

		const content = await FaqStaticContentPresenter.getContent(filters);

		store.mutations.setFaqStaticContent(content);
	},

	clearFilterFor: async (key: FilterKey): Promise<void> => {
		mutations.emptyFilterValues(key);

		EventAggregationService.instance.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent([key]));
		await onFilterChange();
	},

	clearFiltersFor: async (keys: FilterKey[]): Promise<void> => {
		keys.forEach((key) => {
			mutations.emptyFilterValues(key);
		});

		EventAggregationService.instance.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent(keys));

		await onFilterChange();
	},

	clearFilterValues: async (payload: IFilterPayload[]): Promise<void> => {
		payload.forEach((_) => mutations.emptyFilterValue(_.key, _.value));

		EventAggregationService.instance.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent(payload.map((_) => _.key)));
		await onFilterChange();
	},

	clearFilters: async (): Promise<void> => {
		mutations.emptyAllFilterValues();
		mutations.untrackStudyCardIds();

		const keys = getters.getFilters().map(filter => filter.key);

		if (keys?.length) {
			EventAggregationService.instance.publishTo(SearchPageClearFiltersEvent.EventType, new SearchPageClearFiltersEvent(keys));
		}

		await navigateFromState();
	},

	applyFilterSuggestion: async (payload: ISuggestionPayload): Promise<void> => {
		payload.toDeselect.forEach((key: FilterKey) => mutations.emptyFilterValues(key));
		payload.toSelect.forEach((value: IFilterPayload) => mutations.processFilterValue(value));
		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processAutocompleteInput: async (payload: IAutocomplete, emptyCurrentFilter?: boolean): Promise<void> => {
		if (emptyCurrentFilter) {
			mutations.emptyAllFilterValues();
		}

		mutations.replaceFilterValues(payload.filters);
		mutations.untrackStudyCardIds();
		window.scrollTo(0, 0);

		await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), PaginationDefinition.DEFAULT_PAGE_NUMBER);
	},

	processFilterSelection: async (payload: IFilterPayload): Promise<void> => {
		if (getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.ERROR) {
			await store.actions.resetApplication();
		}
		mutations.processFilterValue(payload);
		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processFilters: async (payload: IFilterPayload[]): Promise<void> => {
		if (getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.ERROR) {
			await store.actions.resetApplication();
		}

		payload.forEach((_) => mutations.processFilterValue(_));

		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processPageChange: async (pageNumber: number): Promise<void> => {
		mutations.storePreviousPageNumber();
		mutations.storePageNumber(pageNumber);

		await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), pageNumber);
	},

	processUrlContent: async (payload: IUrlContent): Promise<void> => {
		store.mutations.replaceFilterValues(payload.filters);

		const mergedFilters = store.getters.getFilters();
		const parsedSelectedFilters = mergedFilters.filter((_) => _.values.length > 0).map((_) => _.toParsedFilter());

		const areFiltersEqual = ParsedFilters.areEqual(payload.filters, parsedSelectedFilters);

		if (!areFiltersEqual) {
			await navigateFromState();
			return;
		}

		store.mutations.storePageNumber(payload.pageNumber);
		store.mutations.storeSorting(payload.sorting);

		if (RoutingHelper.canBePrettified(mergedFilters, payload.path)) {
			await navigateFromState();
			return;
		}

		await store.actions.callServices();
	},

	processUserCurrencyAndCountryChange: async (userCountryIso?: string, userCurrency?: string): Promise<void> => {
		let isChanged = false;

		if (userCurrency && userCurrency !== getters.getCurrency()) {
			store.mutations.storeUserCurrency(userCurrency);
			isChanged = true;
		}

		if (userCountryIso && userCountryIso !== getters.getUserCountryIso()) {
			store.mutations.storeUserNationalityIso(userCountryIso);
			isChanged = true;
		}

		if (isChanged) {
			await store.actions.updateSearchResults(await getParameters());
			EventsSender.sendInitialEvents(store.getters.getPortalType());
			mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		}
	},

	processSortingClick: async (sorting: SortingType): Promise<void> => {
		if (sorting !== store.getters.getSorting()) {
			store.mutations.storeSorting(sorting);
			await navigateFromState();
		}
	},

	resetApplication: async (): Promise<void> => {
		await router.push({ name: RoutingDefinitions.ROOT });

		store.mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		store.mutations.untrackStudyCardIds();
	},

	resetApplicationFromErrorPageButton: (): void => {
		store.mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		store.mutations.untrackStudyCardIds();

		void router.push({ name: RoutingDefinitions.ROOT });
	},

	updateSearchResults: async (parameters: ParameterValue[]): Promise<void> => {
		const page = store.state.page;
		mutations.storeApplicationStatus(ApplicationStatusOptions.FETCHING);

		const { results, facets, count } = await SearchResultsPresenter.retrieveSearchResults(parameters,
			store.getters.getPortalType(),
			page.size,
			page.start,
			store.getters.getSorting()
		);

		const resultsPerPage = PaginationDefinition.PAGE_SIZE;
		const totalPages = Math.ceil(count / resultsPerPage);
		const currentPage = getters.getPageNumber();

		if (count > 0 && currentPage > totalPages) {
			mutations.storePageNumber(1);
			await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), 1);
			return;
		}

		const previousResults = getters.getSearchResults();
		const isSameQuery =
			JSON.stringify(getters.getPreviousSearchQuery()) === JSON.stringify(parameters) &&
			store.getters.getPreviousSorting() === store.getters.getSorting();
		const isNextPage = getters.getPageNumber() - getters.getPreviousPageNumber() === 1;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		const experimentIsControl = DataStorage.retrieve('Experiment/PaginationSearchPageExperiment')?.number === 0;

		if (isSameQuery && isNextPage && experimentIsControl) {
			mutations.storeSearchResults([...previousResults, ...results]);
		} else {
			mutations.storeSearchResults(results);
		}

		if (facets) {
			mutations.storeFacets(facets);
		}

		mutations.storeSearchResultsCount(count);

		mutations.storePreviousSearchQuery(JSON.stringify(Array.from(parameters)));
		store.mutations.storePreviousSorting();
		pageTracking();
	},

	updateSearchResultsFromRoute: async (route: RouteLocationNormalized): Promise<void> => {
		if (getters.getApplicationStatus() as ApplicationStatusOptions === ApplicationStatusOptions.INITIALIZING) {
			return;
		}

		const navigationFailed = route.matched.length === 0;
		if (navigationFailed) {
			store.mutations.storeApplicationStatus(ApplicationStatusOptions.ERROR);
		} else {
			await UrlParser.parse(route.params, route.path, route.query, store.state.portalDefinition);
		}
	},


	initializeFrom: (state: IAppState): void => {
		for (const filterKey of Object.keys(state.filterSelection)) {
			const filter = store.state.filters.find((_) => _.key === filterKey as FilterKey);
			if (filter) {
				for (const value of state.filterSelection[filterKey] ?? []) {
					mutations.processFilterValue({ key: filterKey as FilterKey, value });
				}
			}
		}
	},

	activate: async (state: IAppState): Promise<void> => {
		for (const filterKey of Object.keys(state.filterSelection)) {
			const filter = store.state.filters.find((_) => _.key === filterKey as FilterKey);
			if (filter) {
				mutations.emptyFilterValues(filterKey as FilterKey);

				for (const value of state.filterSelection[filterKey] ?? []) {
					mutations.processFilterValue({ key: filterKey as FilterKey, value });
				}
			}
		}

		await navigateFromState();
	},

	sleep: (): IAppState => {
		const state = {
			filterSelection: {},
		};

		for (const filter of store.state.filters.filter((f) => f.values.length > 0)) {
			state.filterSelection[filter.key] = [...filter.values];
		}

		return state;
	},
};

export default actions;
